import { Storage as AmplifyStorage } from "aws-amplify";

import { authService } from "@ds/modules/auth/utils/api";

import { REACT_APP_REGION } from "@ds/constants/environment";

export interface StorageServiceConfig {
  region: string;
  bucket?: string;
}

export interface StorageMethodConfig {
  level?: "public" | "protected" | "private";
  contentType?: string;
  download?: boolean;
  maxKeys?: number;
  progressCallback?: (progressInfo: ProgressInfo) => void;
}

export interface ProgressInfo {
  key?: string;
  name?: string;
  loaded?: number;
  part?: number;
  total?: number;
  isTrusted?: boolean;
}

class StorageService {
  private config: StorageServiceConfig = {
    region: REACT_APP_REGION,
  };

  private pendingOperations = new Set<Promise<unknown>>();

  private track<T>(p: Promise<T>): Promise<T> {
    this.pendingOperations.add(p);
    void p.finally(() => this.pendingOperations.delete(p));
    return p;
  }

  cancelPending() {
    // AmplifyStorage.cancel(promise) is a valid Amplify v5 Storage API (Storage.d.ts overload 2).
    this.pendingOperations.forEach(p => AmplifyStorage.cancel(p, "Operation cancelled on logout"));
    this.pendingOperations.clear();
  }

  configure(cfg: StorageServiceConfig) {
    this.config = { ...this.config, ...cfg };
    AmplifyStorage.configure({
      bucket: this.config.bucket,
      region: this.config.region,
    });
  }

  /**
   * NOTE: All methods must be authenticated.
   * We are forcing a JWT refresh before each request just to be safe.
   * (eg. This covers the case where a user leaves browser tab open for a long time)
   */
  async get(key: string, config?: StorageMethodConfig) {
    await authService.getCurrentSessionJwtToken();
    return this.track(AmplifyStorage.get(key, config) as Promise<string | { Body: Uint8Array; ContentType: string }>);
  }

  async put(key: string, data: unknown, config?: StorageMethodConfig) {
    await authService.getCurrentSessionJwtToken();
    return this.track(AmplifyStorage.put(key, data, config) as unknown as Promise<{ key: string }>);
  }

  async remove(key: string, config?: StorageMethodConfig): Promise<unknown> {
    await authService.getCurrentSessionJwtToken();
    return this.track(AmplifyStorage.remove(key, config) as Promise<unknown>);
  }

  async list(key: string, config?: StorageMethodConfig): Promise<unknown> {
    await authService.getCurrentSessionJwtToken();
    return this.track(AmplifyStorage.list(key, config) as Promise<unknown>);
  }
}

export const storageService = new StorageService();
